import React, { useState, useEffect } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { SAVE_REPORT_DATA, GET_REPORTS, GET_REPORT, GENERATE_REPORT_SHARE_TOKEN } from '../../graphql/reports';
import { ReportType } from './ReportTypeSelector';
import { RoleFilter } from './RoleFilterSelector';
import { exportReportToExcel } from '../../utils/excelExport';
import ExpensesSection from './ExpensesSection';
import EmployeeSummary from './EmployeeSummary';
import ReportJobFlags from './ReportJobFlags';
import HourlyEmployeesSection from './HourlyEmployeesSection';
import ScrollIndicator from '../ScrollIndicator';
import { generatePayrollReportName, getReportTypeDisplayName } from '../../utils/reportNaming';

const ReportView = ({ reportData, reportType, roleFilter, startDate, endDate, savedReportId, savedReportName = null, onBackToSelection, selectedAreas = null, onReportSaved = null, isPublicView = false }) => {
  const isHousekeeping = reportType === ReportType.HOUSEKEEPING;
  const client = useApolloClient();
  
  // Create a deep copy of reportData to avoid read-only issues from Apollo cache
  const createEditableData = (data) => {
    return JSON.parse(JSON.stringify(data));
  };
  
  const [editableData, setEditableData] = useState(() => createEditableData(reportData));
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [expenses, setExpenses] = useState(() => {
    // Initialize expenses from saved report data if available
    if (reportData.expenses) {
      return reportData.expenses;
    }
    return [];
  });
  
  const [hourlyEmployeeHours, setHourlyEmployeeHours] = useState(() => {
    // Initialize hourly employee hours from saved report data if available
    if (reportData.hourlyEmployeeHours || reportData.laundryEmployeeHours) {
      return reportData.hourlyEmployeeHours || reportData.laundryEmployeeHours;
    }
    return [];
  });

  // Share functionality state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  
  // Track the current saved report ID (updates when report is saved)
  const [currentSavedReportId, setCurrentSavedReportId] = useState(savedReportId);
  
  // Accordion state for employee job listings - all closed by default
  const [expandedEmployees, setExpandedEmployees] = useState({});
  
  // Auto-save state
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Update editableData when reportData changes (e.g., when loading a saved report)
  useEffect(() => {
    const newData = createEditableData(reportData);
    setEditableData(newData);
    // Update expenses from saved report
    if (reportData.expenses) {
      setExpenses(reportData.expenses);
    }
    // Update hourly employee hours from saved report (support migration from laundry employees)
    if (reportData.hourlyEmployeeHours || reportData.laundryEmployeeHours) {
      setHourlyEmployeeHours(reportData.hourlyEmployeeHours || reportData.laundryEmployeeHours);
    }
    // Reset unsaved changes when new data is loaded
    setHasUnsavedChanges(false);
  }, [reportData]);

  // Update currentSavedReportId when savedReportId prop changes
  useEffect(() => {
    setCurrentSavedReportId(savedReportId);
  }, [savedReportId]);

  // Add before unload handler to save and warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && savedReportId) {
        // Try to save before leaving
        handleAutoSave('before-unload');
        
        // Browser warning
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, savedReportId]);

  // Update total expenses when expenses change or hourly employee hours change
  useEffect(() => {
    // Calculate manual expenses
    const manualExpenses = expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);
    
    // Calculate employee labor for tax calculation (regular employees + hourly employees)
    const employees = editableData.teamMembers?.filter(member => 
      member.teamMemberId !== 0 && member.isContractor !== true
    ) || [];
    
    const regularEmployeeLabor = employees.reduce((total, member) => 
      total + (Number(member.totalPayout) || 0), 0
    );
    
    // Calculate hourly employee labor (excluding contractors)
    const hourlyEmployeeLabor = hourlyEmployeeHours.reduce((sum, empHours) => {
      if (!empHours.hours || empHours.hours <= 0) return sum;
      // Exclude hourly contractors from employee tax calculation
      if (empHours.isContractor === true) return sum;
      const hours = parseFloat(empHours.hours);
      const rate = empHours.hourlyRate || 0;
      return sum + (rate * hours);
    }, 0);
    
    const totalEmployeeLabor = regularEmployeeLabor + hourlyEmployeeLabor;
    
    // Calculate employee tax (11% of employee labor only, excludes contractors)
    const employeeTax = totalEmployeeLabor * 0.11;
    
    // Total expenses includes manual expenses + employee tax
    const totalExpenses = manualExpenses + employeeTax;
    
    setEditableData(prevData => ({
      ...prevData,
      totalExpenses,
      finalTotal: (prevData.grandTotalPayout || 0) + totalExpenses
    }));
    
    // Mark as having unsaved changes when expenses change (only if editing)
    if (isEditing) {
      setHasUnsavedChanges(true);
    }
  }, [expenses, isEditing, hourlyEmployeeHours, editableData.teamMembers]);

  // Mark as having unsaved changes when hourly employee hours change
  useEffect(() => {
    if (isEditing && hourlyEmployeeHours.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [hourlyEmployeeHours, isEditing]);

  const [saveReportData] = useMutation(SAVE_REPORT_DATA, {
    refetchQueries: [{ query: GET_REPORTS }],
    onCompleted: (data) => {
      // Don't show success message or exit edit mode for auto-saves
      // Manual saves will be handled in handleSaveReport
      setHasUnsavedChanges(false); // Reset unsaved changes after successful save
      
      // Update the current saved report ID if this is a new report
      if (data.saveReportData && data.saveReportData.id) {
        setCurrentSavedReportId(data.saveReportData.id);
      }
      
              // If we're updating an existing report, update the Apollo cache for GET_REPORT
        if (savedReportId && data.saveReportData) {
          // Update the cache for the specific report
          try {
            client.cache.modify({
              id: client.cache.identify({ __typename: 'Report', id: savedReportId }),
              fields: {
                reportDataJson: () => data.saveReportData.reportDataJson,
                totalPayout: () => data.saveReportData.totalPayout,
                totalCost: () => data.saveReportData.totalCost,
                totalExpenses: () => data.saveReportData.totalExpenses,
                finalTotal: () => data.saveReportData.finalTotal,
                jobsCount: () => data.saveReportData.jobsCount
              }
            });
          } catch (error) {
            // Silently handle cache update errors
          }
        }
        
        // Notify parent component that report was saved
        if (onReportSaved && data.saveReportData) {
          onReportSaved(data.saveReportData);
        }
    },
    onError: (error) => {
      console.error('Error saving report:', error);
      toast.error('Failed to save report. Please try again.');
    }
  });

  const [generateReportShareToken] = useMutation(GENERATE_REPORT_SHARE_TOKEN);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    let date;
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Handle YYYY-MM-DD strings by adding time component to avoid UTC interpretation
      date = new Date(dateString + 'T00:00:00');
    } else if (typeof dateString === 'number') {
      // Handle timestamp values - extract UTC date and create local date
      const tempDate = new Date(dateString);
      const year = tempDate.getUTCFullYear();
      const month = tempDate.getUTCMonth();
      const day = tempDate.getUTCDate();
      date = new Date(year, month, day); // Create as local date
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'MMM dd, yyyy');
  };

  const handleFieldEdit = (teamMemberId, jobIndex, field, value) => {
    const numericValue = parseFloat(value) || 0;
    
    setEditableData(prevData => {
      const newData = { ...prevData };
      const teamMemberIndex = newData.teamMembers.findIndex(tm => tm.teamMemberId === teamMemberId);
      
      if (teamMemberIndex !== -1) {
        const updatedTeamMember = { ...newData.teamMembers[teamMemberIndex] };
        const updatedJob = { ...updatedTeamMember.jobs[jobIndex] };
        
        if (field === 'payout') {
          updatedJob.calculatedPayout = numericValue;
          updatedJob.isPayoutOverridden = true;
          updatedJob.customPayout = numericValue;
        } else if (field === 'billedAmount') {
          updatedJob.calculatedBilledAmount = numericValue;
          updatedJob.isBilledAmountOverridden = true;
          updatedJob.customBilledAmount = numericValue;
        }
        
        updatedTeamMember.jobs[jobIndex] = updatedJob;
        
        // Recalculate team member totals
        // Only recalculate if total payout is not overridden
        if (!updatedTeamMember.isTotalPayoutOverridden) {
          // For salaried employees, preserve the salary-based payout instead of summing job payouts
          if (updatedTeamMember.usesSalaryCalculation && updatedTeamMember.calculatedSalaryPayout != null) {
            updatedTeamMember.totalPayout = updatedTeamMember.calculatedSalaryPayout;
          } else {
            updatedTeamMember.totalPayout = updatedTeamMember.jobs.reduce((sum, job) => sum + (job.calculatedPayout || 0), 0);
          }
        }
        updatedTeamMember.totalBilledAmount = updatedTeamMember.jobs.reduce((sum, job) => sum + (job.calculatedBilledAmount || 0), 0);
        
        newData.teamMembers[teamMemberIndex] = updatedTeamMember;
        
        // Recalculate grand totals
        newData.grandTotalPayout = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalPayout || 0), 0);
        newData.grandTotalBilledAmount = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalBilledAmount || 0), 0);
        newData.finalTotal = newData.grandTotalPayout + (newData.totalExpenses || 0);
      }
      
      return newData;
    });
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  // Handle editing employee total payout (override calculated total)
  const handleEmployeeTotalPayoutEdit = (teamMemberId, value) => {
    const numericValue = parseFloat(value) || 0;
    
    setEditableData(prevData => {
      const newData = { ...prevData };
      const teamMemberIndex = newData.teamMembers.findIndex(tm => tm.teamMemberId === teamMemberId);
      
      if (teamMemberIndex !== -1) {
        const updatedTeamMember = { ...newData.teamMembers[teamMemberIndex] };
        
        // Set the override
        updatedTeamMember.totalPayout = numericValue;
        updatedTeamMember.customTotalPayout = numericValue;
        updatedTeamMember.isTotalPayoutOverridden = true;
        
        newData.teamMembers[teamMemberIndex] = updatedTeamMember;
        
        // Recalculate grand totals
        newData.grandTotalPayout = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalPayout || 0), 0);
        newData.finalTotal = newData.grandTotalPayout + (newData.totalExpenses || 0);
      }
      
      return newData;
    });
    
    setHasUnsavedChanges(true);
    
    // Trigger auto-save after a short delay
    setTimeout(() => {
      handleAutoSave('employee-total-edit');
    }, 500);
  };

  // Reset employee total payout to calculated value
  const handleResetEmployeeTotalPayout = (teamMemberId) => {
    setEditableData(prevData => {
      const newData = { ...prevData };
      const teamMemberIndex = newData.teamMembers.findIndex(tm => tm.teamMemberId === teamMemberId);
      
      if (teamMemberIndex !== -1) {
        const updatedTeamMember = { ...newData.teamMembers[teamMemberIndex] };
        
        // Clear the override
        updatedTeamMember.isTotalPayoutOverridden = false;
        updatedTeamMember.customTotalPayout = undefined;
        
        // Recalculate from jobs or salary
        if (updatedTeamMember.usesSalaryCalculation && updatedTeamMember.calculatedSalaryPayout != null) {
          updatedTeamMember.totalPayout = updatedTeamMember.calculatedSalaryPayout;
        } else {
          updatedTeamMember.totalPayout = updatedTeamMember.jobs.reduce((sum, job) => sum + (job.calculatedPayout || 0), 0);
        }
        
        newData.teamMembers[teamMemberIndex] = updatedTeamMember;
        
        // Recalculate grand totals
        newData.grandTotalPayout = newData.teamMembers.reduce((sum, tm) => sum + (tm.totalPayout || 0), 0);
        newData.finalTotal = newData.grandTotalPayout + (newData.totalExpenses || 0);
      }
      
      return newData;
    });
    
    setHasUnsavedChanges(true);
    
    // Trigger auto-save after reset
    setTimeout(() => {
      handleAutoSave('employee-total-reset');
    }, 500);
  };

  const handleSaveReport = async () => {
    setIsSaving(true);
    try {
      // Use saved report name if it exists (preserve custom names), otherwise generate default name
      const reportName = savedReportName || generatePayrollReportName(reportType, roleFilter, selectedAreas);
      
      // Include expenses and hourly employee hours in the report data
      const reportDataWithExpenses = {
        ...editableData,
        expenses,
        hourlyEmployeeHours
      };
      
      // Clean up GraphQL __typename fields before saving
      const cleanData = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key !== '__typename') {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };
      
      const cleanedData = cleanData(reportDataWithExpenses);
      
      await saveReportData({
        variables: {
          input: {
            name: reportName,
            type: reportType,
            startDate: startDate,
            endDate: endDate,
            roleFilter: roleFilter,
            reportDataJson: JSON.stringify(cleanedData),
            totalPayout: cleanedData.grandTotalPayout,
            totalCost: cleanedData.grandTotalBilledAmount,
            totalExpenses: cleanedData.totalExpenses || 0,
            finalTotal: cleanedData.finalTotal || cleanedData.grandTotalPayout,
            jobsCount: cleanedData.totalJobs,
            existingReportId: savedReportId || null
          }
        }
      });
      
      // Manual save success: show message and exit edit mode
      const message = savedReportId ? 'Report updated successfully!' : 'Report saved successfully!';
      toast.success(message);
      setIsEditing(false);
      
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Auto-save function that saves report without user interaction
   * Triggered by specific events: expense add/delete, hourly hours change, accordion close
   */
  const handleAutoSave = async (trigger = 'manual') => {
    // Don't auto-save if:
    // - Already saving
    // - No saved report ID (can't update non-existent report)
    // - No unsaved changes
    // - Not in edit mode (shouldn't happen but safety check)
    if (isSaving || !savedReportId || !hasUnsavedChanges) {
      return;
    }
    
    setIsSaving(true);
    try {
      // Use saved report name if it exists (preserve custom names), otherwise generate default name
      const reportName = savedReportName || generatePayrollReportName(reportType, roleFilter, selectedAreas);
      
      // Include expenses and hourly employee hours in the report data
      const reportDataWithExpenses = {
        ...editableData,
        expenses,
        hourlyEmployeeHours
      };
      
      // Clean up GraphQL __typename fields before saving
      const cleanData = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key !== '__typename') {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };
      
      const cleanedData = cleanData(reportDataWithExpenses);
      
      await saveReportData({
        variables: {
          input: {
            name: reportName,
            type: reportType,
            startDate: startDate,
            endDate: endDate,
            roleFilter: roleFilter,
            reportDataJson: JSON.stringify(cleanedData),
            totalPayout: cleanedData.grandTotalPayout,
            totalCost: cleanedData.grandTotalBilledAmount,
            totalExpenses: cleanedData.totalExpenses || 0,
            finalTotal: cleanedData.finalTotal || cleanedData.grandTotalPayout,
            jobsCount: cleanedData.totalJobs,
            existingReportId: savedReportId
          }
        }
      });
      
      // Update last saved timestamp
      setLastSavedAt(new Date());
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error('Auto-save failed. Your changes are still safe locally.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportExcel = async () => {
    const reportDataWithExpenses = {
      ...editableData,
      expenses,
      hourlyEmployeeHours
    };
    const reportTitle = getReportTitle();
    await exportReportToExcel(reportDataWithExpenses, reportType, roleFilter, startDate, endDate, false, selectedAreas, reportTitle);
    toast.success('Excel report exported successfully!');
  };

  const getReportTitle = () => {
    // Use saved report name if it exists (preserve custom names), otherwise generate default name
    return savedReportName || generatePayrollReportName(reportType, roleFilter, selectedAreas);
  };

  const handleBackToSelection = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? You have unsaved changes that will be lost.'
      );
      if (!confirmed) {
        return;
      }
    }
    onBackToSelection();
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Are you sure you want to cancel? Your changes will be lost.'
      );
      if (!confirmed) {
        return;
      }
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
    // Reset data to original state
    const resetData = createEditableData(reportData);
    setEditableData(resetData);
    if (reportData.expenses) {
      setExpenses(reportData.expenses);
    }
  };

  // Toggle accordion for employee job listings
  const toggleEmployeeAccordion = (teamMemberId) => {
    const wasExpanded = expandedEmployees[teamMemberId];
    
    setExpandedEmployees(prev => ({
      ...prev,
      [teamMemberId]: !prev[teamMemberId]
    }));
    
    // Auto-save when closing an accordion if there are unsaved changes
    if (wasExpanded && hasUnsavedChanges && savedReportId) {
      handleAutoSave('accordion-close');
    }
  };

  // Expand all employee accordions
  const expandAllEmployees = () => {
    const allExpanded = {};
    (editableData.teamMembers || []).forEach(tm => {
      allExpanded[tm.teamMemberId] = true;
    });
    setExpandedEmployees(allExpanded);
  };

  // Collapse all employee accordions
  const collapseAllEmployees = () => {
    setExpandedEmployees({});
  };

  // Wrapper for expense changes that triggers auto-save
  const handleExpensesChange = (newExpenses) => {
    setExpenses(newExpenses);
    setHasUnsavedChanges(true);
    
    // Auto-save after expense change (add/delete/edit)
    if (savedReportId) {
      // Use setTimeout to ensure state is updated before save
      setTimeout(() => handleAutoSave('expense-change'), 100);
    }
  };

  // Wrapper for hourly employee hours changes (no auto-save on change)
  const handleHourlyEmployeeHoursChange = (newHours) => {
    setHourlyEmployeeHours(newHours);
    setHasUnsavedChanges(true);
  };

  // Auto-save when user finishes editing hourly hours (onBlur)
  const handleHourlyEmployeeHoursBlur = () => {
    if (savedReportId && hasUnsavedChanges) {
      // Small delay to ensure state is updated
      setTimeout(() => handleAutoSave('hourly-hours-complete'), 100);
    }
  };

  // Share functionality
  const generateShareableLink = async () => {
    if (!currentSavedReportId) {
      toast.error('Please save the report before sharing');
      return null;
    }

    setIsGeneratingToken(true);
    try {
      // Generate a token for this report
      const { data } = await generateReportShareToken({
        variables: {
          input: { reportId: currentSavedReportId }
        }
      });
      
      // Extract the token from the response
      const token = data.generateReportShareToken.token;
      
      // Create the shareable link with the token
      const baseUrl = window.location.origin;
      const shareLink = `${baseUrl}/shared/reports/${currentSavedReportId}?token=${token}`;
      setShareUrl(shareLink);
      setIsGeneratingToken(false);
      return shareLink;
    } catch (error) {
      console.error('Error generating share token:', error);
      toast.error('Failed to generate share link');
      setIsGeneratingToken(false);
      return null;
    }
  };
  
  // Handle share button click
  const handleShare = async () => {
    const shareLink = await generateShareableLink();
    
    if (!shareLink) return;
    
    // Check if the Web Share API is available (primarily mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${getReportTitle()} - Report`,
          text: `Check out this ${getReportTitle().toLowerCase()}`,
          url: shareLink
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to modal on mobile if share is cancelled
        setShareModalOpen(true);
      }
    } else {
      // On desktop, show a modal with the link
      setShareModalOpen(true);
    }
  };
  
  // Copy to clipboard function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error('Failed to copy link');
    }
  };

  // Print and Share function - redirects to shared report view
  const handlePrintAndShare = async () => {
    if (!currentSavedReportId) {
      toast.error('Please save the report first before printing and sharing');
      return;
    }

    try {
      setIsGeneratingToken(true);
      const result = await generateReportShareToken({
        variables: {
          input: {
            reportId: currentSavedReportId
          }
        }
      });

      if (result.data?.generateReportShareToken) {
        const { token } = result.data.generateReportShareToken;
        const shareUrl = `${window.location.origin}/shared/reports/${currentSavedReportId}?token=${token}`;
        // Redirect to the shared report view
        window.open(shareUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating share token:', error);
      toast.error('Failed to generate share link');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Clean, Minimal Header */}
      <div className="pt-6">
        {/* Top Action Buttons Row */}
        <div className="flex items-center justify-between mb-4 no-print">
          {/* Left side - Back to Reports */}
        <div>
          {!isPublicView && (
          <button
            onClick={handleBackToSelection}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            ← Back to Reports
          </button>
          )}
          </div>
          
          {/* Right side - Action buttons in specified order */}
          <div className="flex items-center gap-3">
          {/* Edit/Save/Cancel Buttons */}
          {!isPublicView && (
            <>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Report
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveReport}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Save Report
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
          
          {!isPublicView && (
            <>
            {/* Export XLS Button */}
            <button
              onClick={handleExportExcel}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export XLS
            </button>
            
            {/* Print & Share Button */}
            <button
              onClick={handlePrintAndShare}
              disabled={isGeneratingToken || !currentSavedReportId}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!currentSavedReportId ? "Save the report first to enable printing and sharing" : "Print and share this report"}
            >
              {isGeneratingToken ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : !currentSavedReportId ? (
                <>
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Save to Print & Share
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print & Share
                </>
              )}
            </button>
            </>
          )}
        </div>
      </div>

        {/* Report Title and Info */}
        <div className={isPublicView ? 'px-4 lg:px-6' : ''}>
          <h1 className="text-2xl font-semibold text-gray-900">
            {getReportTitle()}
          </h1>
          <p className="text-lg font-medium text-gray-700 mt-2">
            {formatDate(startDate)} - {formatDate(endDate)}
          </p>
          {selectedAreas && selectedAreas.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filtered by areas:</span>
              {selectedAreas.map((area, index) => (
                <span key={area} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                  {area.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Public View - Employee Accordions */}
      {isPublicView && (
        <div className="lg:px-6">
          {/* Header and Expand/Collapse All Controls */}
          <div className="flex items-center justify-between mb-4 px-2 lg:px-0">
            <h3 className="text-lg font-medium text-gray-900">Job Details</h3>
            <div className="flex space-x-2 no-print">
              <button
                onClick={expandAllEmployees}
                className="px-3 py-1 text-sm font-medium text-sky-600 hover:text-sky-700 border border-sky-600 rounded-md hover:bg-sky-50"
              >
                Expand All
              </button>
              <button
                onClick={collapseAllEmployees}
                className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Collapse All
              </button>
            </div>
          </div>
          
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-2 lg:px-4 py-3">
            <div className="flex items-center">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '50%' }}>
                Name
              </div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                Jobs
              </div>
              {isHousekeeping && (
                <>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right" style={{ width: '17.5%' }}>
                    Payout
                  </div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right" style={{ width: '17.5%' }}>
                    Billed
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Employee Accordions - Only show employees with jobs */}
          {(editableData.teamMembers || []).filter(tm => tm.jobsCount > 0).map((teamMember, index) => {
            const isExpanded = expandedEmployees[teamMember.teamMemberId] || false;
            const isEvenRow = index % 2 === 0;
            
            return (
              <div key={teamMember.teamMemberId} className={isEvenRow ? 'bg-gray-50' : 'bg-white'}>
                {/* Accordion Header - Clickable */}
                <div 
                  className={`px-2 lg:px-4 py-3 border-b border-gray-200 cursor-pointer transition-colors ${isEvenRow ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}`}
                  onClick={() => toggleEmployeeAccordion(teamMember.teamMemberId)}
                >
                  <div className="flex items-center">
                    {/* Icon + Name Section - 50% */}
                    <div className="flex items-center space-x-3" style={{ width: '50%' }}>
                      {/* Expand/Collapse Icon */}
                      <svg 
                        className={`h-3 w-3 text-gray-500 transform transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div className="">
                        <h3 className="text-sm text-gray-900">
                          {teamMember.teamMemberName}
                          {teamMember.usesSalaryCalculation && teamMember.weeklySalary && (
                            <span className="ml-2 text-blue-600 font-medium">(Salaried)</span>
                          )}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Jobs Column - 15% */}
                    <div className="text-sm text-gray-900" style={{ width: '15%' }}>
                      <span>{teamMember.jobsCount}</span>
                    </div>
                    
                    {isHousekeeping && (
                      <>
                        {/* Payout Column - 17.5% */}
                        <div className="text-sm text-right" style={{ width: '17.5%' }}>
                          <span className={teamMember.usesSalaryCalculation ? 'text-blue-600 font-medium' : 'text-gray-900'}>
                            {formatCurrency(teamMember.totalPayout)}
                          </span>
                        </div>
                        
                        {/* Billed Column - 17.5% */}
                        <div className="text-sm text-gray-900 text-right" style={{ width: '17.5%' }}>
                          <span>{formatCurrency(teamMember.totalBilledAmount)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Accordion Content - Jobs Table */}
                {isExpanded && (
                  <ScrollIndicator>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[230px]">
                    Property
                  </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[175px]">
                            Service Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    Notes
                  </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                    Flags
                  </th>
                          {isHousekeeping && (
                            <>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payout
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billed Amount
                  </th>
                            </>
                          )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                        {teamMember.jobs.map((jobData, index) => (
                          <tr key={jobData.job.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 text-sm text-gray-900 w-[230px]">
                        <div>
                          <div className="font-medium break-words">{jobData.job.property.name}</div>
                          <div className="text-gray-500 break-words">{jobData.job.property.propertyCode}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[175px]">
                        <div className="truncate">
                          {jobData.job.serviceType?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(jobData.job.scheduledDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 min-w-[180px]">
                        <div className="whitespace-pre-wrap break-words">
                          {jobData.job.notes || 'No notes'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[150px]">
                        <ReportJobFlags 
                          isEarlyCheckin={jobData.job.isEarlyCheckin}
                          isLateCheckout={jobData.job.isLateCheckout}
                          isOwnerBlock={jobData.job.isOwnerBlock}
                          isMaintenanceBlock={jobData.job.isMaintenanceBlock}
                        />
                      </td>
                            {isHousekeeping && (
                              <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(jobData.calculatedPayout || jobData.customPayout || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(jobData.calculatedBilledAmount || jobData.customBilledAmount || 0)}
                      </td>
                              </>
                )}
                          </tr>
                        ))}
              </tbody>
                      {isHousekeeping && (
                        <tfoot className="bg-blue-50">
                          <tr>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              Total
                            </td>
                            <td colSpan="4" className="px-6 py-4"></td>
                            <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                              <div className="flex items-center justify-end">
                                {formatCurrency(teamMember.totalPayout)}
                                {teamMember.isTotalPayoutOverridden && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Override
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                              {formatCurrency(teamMember.totalBilledAmount)}
                            </td>
                          </tr>
                          {teamMember.usesSalaryCalculation && teamMember.weeklySalary && (
                            <tr>
                              <td colSpan="7" className="px-6 py-3 text-sm">
                                <div className="text-blue-600 font-medium">
                                  Salary: {formatCurrency(teamMember.weeklySalary)}/week
                                </div>
                                {teamMember.salaryCalculationMethod && (
                                  <div className="text-gray-600 mt-1">
                                    {teamMember.salaryCalculationMethod}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </tfoot>
                      )}
            </table>
                  </ScrollIndicator>
                )}
          </div>
            );
          })}
        </div>
      )}


      {/* Team Members Reports - Only show in non-public view */}
      {!isPublicView && (
      <div className="space-y-4">
        {/* Header and Expand/Collapse All Controls */}
        <div className="flex items-center justify-between px-2 lg:px-0">
          <h2 className="text-lg font-medium text-gray-900">Job Details</h2>
          <div className="flex space-x-2">
            <button
              onClick={expandAllEmployees}
              className="px-3 py-1 text-sm font-medium text-sky-600 hover:text-sky-700 border border-sky-600 rounded-md hover:bg-sky-50"
            >
              Expand All
            </button>
            <button
              onClick={collapseAllEmployees}
              className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Table Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-2 lg:px-6 py-3 rounded-t-lg">
          <div className="flex items-center">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '50%' }}>
              Name
            </div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
              Jobs
            </div>
            {isHousekeeping && (
              <>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right" style={{ width: '17.5%' }}>
                  Payout
                </div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right" style={{ width: '17.5%' }}>
                  Billed
                </div>
              </>
            )}
          </div>
        </div>

        {/* Employee Accordions - Only show employees with jobs */}
        {(editableData.teamMembers || []).filter(tm => tm.jobsCount > 0).map((teamMember, index) => {
          const isExpanded = expandedEmployees[teamMember.teamMemberId] || false;
          const isEvenRow = index % 2 === 0;
          
          return (
            <div key={teamMember.teamMemberId} className={`rounded-lg shadow ${isEvenRow ? 'bg-gray-50' : 'bg-white'}`}>
              {/* Accordion Header - Clickable */}
              <div 
                className={`px-2 lg:px-6 py-4 border-b border-gray-200 cursor-pointer transition-colors ${isEvenRow ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}`}
                onClick={() => toggleEmployeeAccordion(teamMember.teamMemberId)}
              >
                <div className="flex items-center">
                  {/* Icon + Name Section - 50% */}
                  <div className="flex items-center space-x-3" style={{ width: '50%' }}>
                    {/* Expand/Collapse Icon */}
                    <svg 
                      className={`h-5 w-5 text-gray-500 transform transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                <div>
                      <h3 className="text-base font-medium text-gray-900">
                        {teamMember.teamMemberName}
                  {teamMember.usesSalaryCalculation && teamMember.weeklySalary && (
                          <span className="ml-2 text-blue-600 font-medium">(Salaried)</span>
                        )}
                      </h3>
                    </div>
                </div>
                  
                  {/* Jobs Column - 15% */}
                  <div className="text-sm text-gray-900" style={{ width: '15%' }}>
                  <span>{teamMember.jobsCount} jobs</span>
                  </div>
                  
                  {isHousekeeping && (
                    <>
                      {/* Payout Column - 17.5% */}
                      <div 
                        className="text-sm text-right flex items-center justify-end" 
                        style={{ width: '17.5%' }}
                        onClick={(e) => isEditing && e.stopPropagation()}
                      >
                        {isEditing ? (
                          <div className="flex items-center">
                            <input
                              type="number"
                              step="0.01"
                              value={teamMember.totalPayout || 0}
                              onChange={(e) => handleEmployeeTotalPayoutEdit(teamMember.teamMemberId, e.target.value)}
                              className={`w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 ${teamMember.usesSalaryCalculation ? 'text-blue-600 font-medium' : 'text-gray-900'}`}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {teamMember.isTotalPayoutOverridden && (
                              <div className="flex items-center ml-2">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Override
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetEmployeeTotalPayout(teamMember.teamMemberId);
                                  }}
                                  className="ml-1 text-xs text-gray-600 hover:text-gray-900 underline"
                                  title="Reset to calculated value"
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className={teamMember.usesSalaryCalculation ? 'text-blue-600 font-medium' : 'text-gray-900'}>
                              {formatCurrency(teamMember.totalPayout)}
                            </span>
                            {teamMember.isTotalPayoutOverridden && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Override
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Billed Column - 17.5% */}
                      <div className="text-sm text-gray-900 text-right" style={{ width: '17.5%' }}>
                        <span>{formatCurrency(teamMember.totalBilledAmount)}</span>
                      </div>
                    </>
                  )}
              </div>
            </div>
            
              {/* Accordion Content - Jobs Table */}
              {isExpanded && (
                <ScrollIndicator>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[230px]">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[175px]">
                      Service Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                      Notes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                      Flags
                    </th>
                    {isHousekeeping && (
                      <>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payout
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Billed Amount
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMember.jobs.map((jobData, index) => (
                    <tr key={jobData.job.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 text-sm text-gray-900 w-[230px]">
                        <div>
                          <div className="font-medium break-words">{jobData.job.property.name}</div>
                          <div className="text-gray-500 break-words">{jobData.job.property.propertyCode}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[175px]">
                        <div className="truncate">
                          {jobData.job.serviceType?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(jobData.job.scheduledDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 min-w-[180px]">
                        <div className="whitespace-pre-wrap break-words">
                          {jobData.job.notes || 'No notes'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[150px]">
                        <ReportJobFlags 
                          isEarlyCheckin={jobData.job.isEarlyCheckin}
                          isLateCheckout={jobData.job.isLateCheckout}
                          isOwnerBlock={jobData.job.isOwnerBlock}
                          isMaintenanceBlock={jobData.job.isMaintenanceBlock}
                        />
                      </td>
                      {isHousekeeping && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            <div className="flex items-center justify-end">
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={jobData.calculatedPayout || 0}
                                  onChange={(e) => handleFieldEdit(teamMember.teamMemberId, index, 'payout', e.target.value)}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500 text-right"
                                />
                              ) : (
                                formatCurrency(jobData.calculatedPayout)
                              )}
                              {jobData.isPayoutOverridden && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Override
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            <div className="flex items-center justify-end">
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={jobData.calculatedBilledAmount || 0}
                                  onChange={(e) => handleFieldEdit(teamMember.teamMemberId, index, 'billedAmount', e.target.value)}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500 text-right"
                                />
                              ) : (
                                formatCurrency(jobData.calculatedBilledAmount)
                              )}
                              {jobData.isBilledAmountOverridden && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Override
                                </span>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
                </ScrollIndicator>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Hourly Employees Section - Only for Housekeeping Reports */}
      {isHousekeeping && (isEditing || hourlyEmployeeHours.some(emp => emp.hours && parseFloat(emp.hours) > 0)) && (
        <HourlyEmployeesSection 
          hourlyEmployeeHours={hourlyEmployeeHours} 
          onHourlyEmployeeHoursChange={handleHourlyEmployeeHoursChange}
          onHourlyEmployeeHoursBlur={handleHourlyEmployeeHoursBlur}
          isEditing={isEditing}
        />
      )}

      {/* Employee Summary */}
      <EmployeeSummary 
        teamMembers={editableData.teamMembers || []} 
        reportType={reportType}
        hourlyEmployeeHours={hourlyEmployeeHours}
        isEditing={isEditing}
        onEmployeeTotalPayoutEdit={handleEmployeeTotalPayoutEdit}
        onResetEmployeeTotalPayout={handleResetEmployeeTotalPayout}
      />

      {/* Expenses Section - Only for Housekeeping Reports */}
      {isHousekeeping && (() => {
        // Calculate employee labor total (excluding contractors) for employee tax calculation
        const employees = editableData.teamMembers?.filter(member => 
          member.teamMemberId !== 0 && member.isContractor !== true
        ) || [];
        
        // Calculate regular employee labor
        const regularEmployeeLabor = employees.reduce((total, member) => 
          total + (Number(member.totalPayout) || 0), 0
        );
        
        // Calculate hourly employee labor (excluding contractors)
        const hourlyEmployeeLabor = hourlyEmployeeHours.reduce((sum, empHours) => {
          if (!empHours.hours || empHours.hours <= 0) return sum;
          // Exclude hourly contractors from employee tax calculation
          if (empHours.isContractor === true) return sum;
          // Hours and rate are stored in hourlyEmployeeHours array
          const hours = parseFloat(empHours.hours);
          const rate = empHours.hourlyRate || 0;
          return sum + (rate * hours);
        }, 0);
        
        // Total employee labor includes both regular employees and hourly employees (excluding contractors)
        const employeeLaborTotal = regularEmployeeLabor + hourlyEmployeeLabor;
        
        return (
        <ExpensesSection 
          expenses={expenses} 
          onExpensesChange={handleExpensesChange}
          isEditing={isEditing}
          employeeLaborTotal={employeeLaborTotal}
          isPublicView={isPublicView}
        />
        );
      })()}

      {/* Professional Report Totals */}
      <div className="bg-white lg:p-6 mb-8">
        <div className="px-6 py-4 border-b border-gray-200 px-2 lg:px-0">
          <h3 className="text-xl font-medium text-gray-900">Report Summary</h3>
        </div>
        
        <div className="overflow-x-auto">
          {(() => {
            // Calculate employee and contractor labor costs
            const employees = editableData.teamMembers?.filter(member => 
              member.teamMemberId !== 0 && member.isContractor !== true
            ) || [];
            
            const contractors = editableData.teamMembers?.filter(member => 
              member.teamMemberId !== 0 && member.isContractor === true
            ) || [];
            
            // Calculate hourly employee labor costs (excluding contractors)
            const hourlyEmployeeLabor = hourlyEmployeeHours.reduce((sum, empHours) => {
              if (!empHours.hours || empHours.hours <= 0) return sum;
              // Exclude hourly contractors
              if (empHours.isContractor === true) return sum;
              const hours = parseFloat(empHours.hours);
              const rate = empHours.hourlyRate || 0;
              return sum + (rate * hours);
            }, 0);
            
            // Calculate hourly contractor labor costs
            const hourlyContractorLabor = hourlyEmployeeHours.reduce((sum, empHours) => {
              if (!empHours.hours || empHours.hours <= 0) return sum;
              // Only include hourly contractors
              if (empHours.isContractor !== true) return sum;
              const hours = parseFloat(empHours.hours);
              const rate = empHours.hourlyRate || 0;
              return sum + (rate * hours);
            }, 0);
            
            const employeeLabor = employees.reduce((sum, member) => sum + (Number(member.totalPayout) || 0), 0) + hourlyEmployeeLabor;
            const contractorLabor = contractors.reduce((sum, member) => sum + (Number(member.totalPayout) || 0), 0) + hourlyContractorLabor;
            const totalBilled = editableData.grandTotalBilledAmount || 0;
            const totalExpenses = editableData.totalExpenses || 0;
            const netProfit = totalBilled - employeeLabor - contractorLabor - totalExpenses;
            const profitPercentage = totalBilled > 0 ? (netProfit / totalBilled * 100) : 0;

            return (
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Total Billed
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                      {formatCurrency(totalBilled)}
                    </td>
                  </tr>
                
                {isHousekeeping && (
                  <>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          Employee Labor
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          {formatCurrency(employeeLabor)}
                        </td>
                      </tr>
                      
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          Contractor Labor
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          {formatCurrency(contractorLabor)}
                        </td>
                      </tr>
                      
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          Expenses
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          {formatCurrency(totalExpenses)}
                        </td>
                      </tr>
                      
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          Net Profit
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className={`text-sm font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(netProfit)}
                          </div>
                          <div className={`text-xs font-medium ${profitPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {profitPercentage >= 0 ? '+' : ''}{profitPercentage.toFixed(1)}%
                          </div>
                        </td>
                      </tr>
                  </>
                )}
                
                {!isHousekeeping && (
                    <tr>
                      <td colSpan="2" className="px-6 py-8 text-center text-sm text-gray-500">
                    Financial details available for Housekeeping reports only
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            );
          })()}
        </div>
      </div>

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Share Report</h3>
                <button
                  onClick={() => setShareModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Link
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(shareUrl)}
                    className="px-4 py-2 bg-sky-600 text-white rounded-r-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="text-sm text-gray-500 mb-4">
                This link will expire in 30 days and can be shared with anyone.
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShareModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView; 